import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  Box,
  createTheme,
  ThemeProvider,
} from "@mui/material";

import CHLogo from "assets/images/CH_logo.svg";
import { useAuth } from "../../hooks/useAuth";
import { QRCodeCanvas } from "qrcode.react";
import ViewBuyProduct from "components/Modal/Pawn/View/ViewBuyItems";
import ReceiptHeader from "./ReceiptHeader";
import ReceiptFooter from "./ReceiptFooter";
import TableProductReceipt from "./TableProductReceipt";
import { formatCurrency2Digit } from "utils/helpers/format";

const theme = createTheme({
  typography: {
    fontFamily: "Sarabun, sans-serif",
    fontSize: 10,
    h6: {
      fontSize: 16,
      fontWeight: "bold",
    },
  },
});

const InvoiceForm = ({ data, vat }) => {
  console.log("data4399", data);

  const rowsPerPage = 9;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [productData, setProductData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  // const { user } = useAuth();
  // console.log({ user });
  const calculateTotalAmount = (products) => {
    return products.reduce((total, product) => {
      return (
        total +
        (parseFloat(product.sell_price) * parseFloat(product.amount) || 0)
      );
    }, 0);
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000); // Update every second

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (data) {
      // Collect products based on the numbered suffix in data keys

      const products = [
        {
          product_id: data.product_id,
          product_code: data.product_code,
          product_code_com: data.product_code_com,
          product_name: data.product_name,
          category_id: data.category_id,
          detail: data.detail,
          blemishes: data.blemishes,
          size: data.size,
          amount: data.amount,
          total_amount: data.total_amount,
          price: data.price,
          sell_price: data.sell_price,
          discount_price: data.discount_price,
          total_price: data.total_price,
          images: data.images,
          payment_channel: data.payment_channel,
          notes: data.notes,
        },
      ];

      setProductData(products);
    }
  }, [data]);

  // Function to split the productData into boxes of rowsPerPage
  const splitIntoBoxes = (data, rowsPerPage) => {
    let boxes = [];
    for (let i = 0; i < data.length; i += rowsPerPage) {
      boxes.push(data.slice(i, i + rowsPerPage));
    }
    return boxes;
  };

  // Prepare the boxes using the filtered product data
  const boxes = splitIntoBoxes(productData, rowsPerPage);
  useEffect(() => {
    // ใช้ useEffect เพื่อคำนวณและอัพเดท totalAmount ทุกครั้งที่ boxes เปลี่ยนแปลง
    const newTotalAmount = boxes.reduce((total, boxData) => {
      return total + calculateTotalAmount(boxData);
    }, 0);

    setTotalAmount(newTotalAmount); // อัพเดท totalAmount ใน state
  }, [boxes]);

  return (
    <ThemeProvider theme={theme}>
      {boxes.map((boxData, boxIndex) => (
        <Box
          key={boxIndex}
          p={3}
          mb={2}
          border={1}
          bgcolor={"background.paper"}
          borderColor="grey.300"
          borderRadius={2}
          height={"559px"}
          width={"794px"}
          position="relative"
        >
          {/* Header Information */}
          <ReceiptHeader
            data={data}
            // date={currentDate}
            label={vat ? "ใบกำกับภาษี" : "ใบเสร็จรับเงิน"}
          />

          {/* Table */}
          <TableProductReceipt
            products={boxData}
            columns={[
              { label: "รายการขาย", key: "index" },
              {
                label: "",
                key: "product_name",
                render: (_, product) => (
                  <>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        display: "flex",
                      }}
                    >
                      {product.product_name || "-"}
                    </Typography>
                    <Typography>
                      {product.detail ? `(${product.detail})` : "-"}
                    </Typography>
                  </>
                ),
              },

              { label: "รหัส1", key: "product_code" },
              { label: "รหัส2", key: "product_code_com" },
              {
                label: "ราคา/ต่อหน่วย",
                key: "sell_price",
              },
              { label: "จำนวน", key: "amount" },
              { label: "ส่วนลด", key: "discount_price" },
              {
                label: "มูลค่ารวม",
                key: "total_price",
                // Custom render function to calculate total sell price
                render: (_, product) => (
                  <Typography sx={{ fontWeight: "bold" }}>
                    {(
                      parseFloat(product.sell_price || 0) *
                        parseFloat(product.amount || 1) -
                      parseFloat(product.discount_price ?? 0)
                    ).toLocaleString() + " บาท"}
                  </Typography>
                ),
              },
            ]}
          />

          {/* Box count display (e.g., "1/3", "2/3") */}
          <ReceiptFooter index={boxIndex} length={boxes.length} />
        </Box>
      ))}
      <Box
        p={3}
        border={1}
        bgcolor={"background.paper"}
        borderColor="grey.300"
        borderRadius={2}
        height={"559px"}
        width={"794px"}
        position={"relative"}
      >
        <Grid container>
          {/* Header Information */}
          <ReceiptHeader
            data={data}
            // date={currentDate}
            label={vat ? "ใบกำกับภาษี" : "ใบเสร็จรับเงิน"}
          />

          {/* Payment Information */}
          <Grid container xs={12} sx={{ mt: 10 }}>
            <Grid
              item
              xs={6}
              container
              justifyContent="center"
              alignItems="center"
            >
              <Typography align="center" style={{}}>
                ชำระเงินโดย :{" "}
                {data?.payment_channel ? data.payment_channel.id : "เงินสด"}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <Typography align="right" style={{}}>
                    รวม
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right" style={{}}>
                    {formatCurrency2Digit(totalAmount)} บาท
                  </Typography>
                </Grid>
              </Grid>

              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <Typography align="right" style={{}}>
                    หักส่วนลดพิเศษ{" "}
                    {data?.discount_rate ? data?.discount_rate : 0}%
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right" style={{}}>
                    {formatCurrency2Digit(
                      data?.discount_price ? data?.discount_price : 0
                    )}{" "}
                    บาท
                  </Typography>
                </Grid>
              </Grid>
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <Typography align="right" style={{}}>
                    ราคาหลังหักส่วนลด
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right" style={{}}>
                    {formatCurrency2Digit(
                      data?.total_discount_price
                        ? data?.total_discount_price
                        : 0
                    )}{" "}
                    บาท
                  </Typography>
                </Grid>
              </Grid>
              {/* {vat ? ( */}
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <Typography align="right" style={{}}>
                    Vat (7%)
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right" style={{}}>
                    {formatCurrency2Digit(
                      data?.total_discount_price
                        ? (data?.total_discount_price * 7) / 100
                        : 0
                    )}{" "}
                    บาท
                  </Typography>
                </Grid>
              </Grid>
              {/* ) : null} */}
            </Grid>
          </Grid>

          {/* Summary Information */}
          <Grid
            container
            xs={12}
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}
          >
            <Grid item xs={6}>
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <Typography align="right" style={{ fontWeight: "bold" }}>
                    รวมจำนวนเงินรายการขายทั้งสิ้น
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right" style={{ fontWeight: "bold" }}>
                    {data?.order_total_price != null
                      ? parseFloat(
                          parseFloat(data.order_total_price) +
                            (parseFloat(data.total_discount_price) * 7) / 100
                        ).toFixed(2)
                      : "ไม่ทราบราคา"}{" "}
                    บาท
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Footer */}
          <Box
            sx={{
              mt: 6,
              mb: 2,
              border: "1px solid #00BB9F",
              borderRadius: "12px",
              width: "100%",
            }}
          >
            <Grid container sx={{ height: "100%" }}>
              {/* Column 1 */}
              <Grid
                item
                xs={4}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRight: "1px solid #00BB9F",
                  padding: "10px",
                }}
              >
                {/* Top Divider Line */}
                <Box
                  sx={{
                    width: "100%",
                    borderTop: "1px solid #B0B0B0",
                    mb: 1,
                    mt: 5,
                  }}
                />
                <Typography align="center" style={{ fontSize: "12px" }}>
                  ลูกค้า (ผู้ขาย)
                </Typography>
                <Typography align="center" style={{}}>
                  {currentDate.toLocaleDateString()}
                </Typography>
              </Grid>

              {/* Column 2 */}
              <Grid
                item
                xs={4}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRight: "1px solid #00BB9F",
                  padding: "10px",
                  height: "100%", // Ensure the column takes full height
                }}
              >
                {/* Top Divider Line */}
                <Box
                  sx={{
                    width: "100%",
                    borderTop: "1px solid #B0B0B0",
                    mb: 1,
                    mt: 5,
                  }}
                />
                <Typography align="center" style={{ fontSize: "12px" }}>
                  ร้านค้า (ซื้อ)
                </Typography>
                <Typography align="center" style={{}}>
                  {currentDate.toLocaleDateString()}
                </Typography>
              </Grid>

              {/* Column 3 */}
              <Grid
                item
                xs={4}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "10px",
                  height: "100%", // Ensure the column takes full height
                }}
              >
                {/* Top Divider Line */}
                <Box
                  sx={{
                    width: "100%",
                    borderTop: "1px solid #B0B0B0",
                    mb: 1,
                    mt: 5,
                  }}
                />
                <Typography align="center" style={{ fontSize: "12px" }}>
                  ผู้อนุมัติ
                </Typography>
                <Typography align="center" style={{}}>
                  {currentDate.toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Box count display (e.g., "1/3", "2/3") */}
        <ReceiptFooter index={boxes.length} length={boxes.length} />
      </Box>
    </ThemeProvider>
  );
};

export default InvoiceForm;
